import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'

import { useTranslation } from '../../../context/translationProvider'
import { AuthContext } from '../../../context/authProvider'
import { AmountRange } from '../../atoms/amountRange'
import { Button } from '../../atoms/button'
import { DrawerSectionTitle } from '../../atoms/drawerSectionTitle'
import { DrawerSectionSubtitle } from '../../atoms/drawerSectionSubtitle'
import { ValueInput } from '../../atoms/valueInput'
import { WithdrawBankAccountList } from '../../organisms/WithdrawBankAccountList'
import useNavbarHeight from '../../../hooks/useNavbarHeight'
import { PaymentTypes } from '../../../utils/constants'
import {
  getLivenessStatus,
  requestBankAccountWithdraw,
} from '../../../adapters/payments'
import { toast } from '../../atoms'
import { usePreDepositModals } from '../../../hooks/usePreDepositModals'
import { BANK_ACCOUNT_TYPES } from '../../../utils/constants'
import { StyledWithdrawForm } from '../../organisms/WithdrawDrawer/style'
import { useEventTracking } from '../../../context/eventTrackingProvider'
import { callWithdrawalWebhook } from '../../../helpers/callWithdrawalWebhook'

const DEFAULT_AMOUNT = 50

const ERROR_CODES = {
  NOT_ENOUGH_BALANCE: 46,
  NOT_ALLOWED_FOR_WITHDRAW: 43,
}

const EXPERIMENT_DETAILS = {
  experiment: 'Withdrawal Flow V1',
  featureId: 'withdrawal_new_flow',
  experiment_group: 'Control',
}

export const BankAccountWithdrawForm = ({
  method,
  minimumAmount,
  maximumAmount,
  bankAccounts = [],
  startLoading,
  stopLoading,
  isMobile,
  isLoading,
}) => {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
  })
  const { translate } = useTranslation()
  const withdrawAmount = watch('withdrawAmount')
  const [selectedBank, setSelectedBank] = useState()
  const { openLivenessModal } = usePreDepositModals()
  const { user } = useContext(AuthContext)
  const currencySymbol = user?.wallet?.currency?.symbol
  const navbarHeight = useNavbarHeight()
  const { trackEvent, EVENT_NAMES } = useEventTracking()

  useEffect(() => {
    trackEvent(EVENT_NAMES.WITHDRAWAL_AMOUNT_LOADED, {}, true)
    trackEvent(
      EVENT_NAMES.GROWTHBOOK_EXPERIMENT,
      { ...EXPERIMENT_DETAILS },
      true
    )
  }, [])

  useEffect(() => {
    const defaultBankAccount = bankAccounts.find(
      (account) => account.defaultForWithdrawal
    )

    if (defaultBankAccount) {
      setSelectedBank(defaultBankAccount)
    } else if (bankAccounts.length > 0) {
      setSelectedBank(bankAccounts[0])
    }
  }, [bankAccounts])

  const onWithdraw = async () => {
    trackEvent(EVENT_NAMES.WITHDRAWAL_REQUESTED, {}, true)
    startLoading()

    const response = await requestBankAccountWithdraw({
      bankAccountId: selectedBank.id,
      amount: Number(withdrawAmount),
      paymentMethodDetailId: method.paymentMethodDetailId,
    })

    if (!response.ok) {
      stopLoading()

      if (response?.error?.code === ERROR_CODES.NOT_ENOUGH_BALANCE) {
        return toast.error(
          translate('payments.withdraw.error.notEnoughBalance')
        )
      }

      if (
        response?.error?.code === ERROR_CODES.NOT_ALLOWED_FOR_WITHDRAW
      ) {
        return toast.error(translate('payments.withdraw.error.notAllowed'))
      }

      return toast.error(response.error.message)
    }

    stopLoading()

    const {
      data: {
        id: transactionId,
        liveness: { url, onboardingId },
      },
    } = response
    
    trackEvent(
      EVENT_NAMES.LIVENESS_STARTED,
      { liveness_reason: 'Withdrawal' },
      true
    )
    openLivenessModal({
      bankAccount: selectedBank,
      onCancelClose: () => {},
      onSuccessSave: () => pollLivenessStatus(onboardingId, transactionId),
      openOrigin: PaymentTypes.WITHDRAW,
      customURL: url,
      showSuccessToast: false,
    })
  }

  const pollLivenessStatus = async (onboardingId, transactionId) => {
    const response = await getLivenessStatus(onboardingId)

    if (!response.ok) {
      toast.error(response.error.message)
      return
    }

    const {
      data: { status },
    } = response

    if (status === 'APPROVED') {
      toast.success(translate('payments.withdraw.withdrawalSuccessful'))
      trackEvent(
        EVENT_NAMES.LIVENESS_COMPLETED,
        { liveness_reason: 'Withdrawal' },
        true
      )
      trackEvent(
        EVENT_NAMES.WITHDRAWAL_REQUEST_SUCCESSFUL,
        {
          ...EXPERIMENT_DETAILS,
          transaction_id: transactionId,
        },
        true
      )
      callWithdrawalWebhook(transactionId, 'Control')
      return
    }

    if (status === 'REPROVED') {
      toast.error(translate('payments.receipt.error'))
      return
    }

    setTimeout(() => pollLivenessStatus(onboardingId, transactionId), 500)
  }

  const validateAmount = (value) => {
    if (!value) return true

    const isBelowMinimum = value < minimumAmount && value !== 0
    const isAboveBalance = value > user?.wallet?.amount
    const isAboveMaximum = value > maximumAmount

    if (isBelowMinimum) {
      return `${translate(
        'payments.minimumWithdrawalAmount'
      )} ${currencySymbol}${minimumAmount}`
    }

    if (isAboveBalance) {
      return translate('payments.withdraw.notEnoughBalance')
    }

    if (isAboveMaximum) {
      return `${translate(
        'payments.maximumWithdrawalAmount'
      )} ${currencySymbol}${maximumAmount}`
    }

    return true
  }

  const isValidAmount =
    withdrawAmount > 0 &&
    withdrawAmount > minimumAmount &&
    withdrawAmount <= user?.wallet?.amount &&
    withdrawAmount <= maximumAmount

  const isSubmitDisabled =
    !isValidAmount ||
    isLoading ||
    selectedBank?.accountType === BANK_ACCOUNT_TYPES.SALARY_ACCOUNT

  return (
    <>
    <StyledWithdrawForm 
      onSubmit={handleSubmit(onWithdraw)}
      isMobile={isMobile}
      navbarHeight={navbarHeight}
    >
      <DrawerSectionTitle title={translate('depositAmountTitle1')} />
      <DrawerSectionSubtitle subtitle={translate('depositAmountTitle2')} />
        <Controller
          name="withdrawAmount"
          control={control}
          rules={{
            required: true,
            validate: validateAmount,
          }}
          render={({ field }) => (
            <ValueInput
              {...field}
              forceValue={withdrawAmount}
              placeholder={DEFAULT_AMOUNT}
              currencySymbol={currencySymbol}
              errors={errors?.withdrawAmount}
              disabled={false}
              inputMode="numeric"
            />
          )}
        />

        <AmountRange
          minimumAmount={minimumAmount}
          maximumAmount={maximumAmount}
        />

        <WithdrawBankAccountList
          selectedBank={selectedBank}
          setSelectedBank={setSelectedBank}
          bankAccounts={bankAccounts}
        />

        <Button
          expand
          disabled={isSubmitDisabled}
          id="btnWithdrawalModal"
          isLoading={isLoading}
        >
          {translate('withdraw.viaPix')?.replace(
            '{0}',
            withdrawAmount ? `${currencySymbol}${withdrawAmount}` : ''
          )}
        </Button>
      </StyledWithdrawForm>
    </>
  )
}

BankAccountWithdrawForm.propTypes = {
  method: PropTypes.object,
  minimumAmount: PropTypes.number,
  maximumAmount: PropTypes.number,
  bankAccounts: PropTypes.array,
  startLoading: PropTypes.func,
  stopLoading: PropTypes.func,
  isMobile: PropTypes.bool,
  isLoading: PropTypes.bool,
}
